import { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";

// icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";

// API
import loginApi from "../../../api/loginApi";

// assets
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import { useUserContext } from "context/UserContext";

function Basic() {
  const navigate = useNavigate();
  const { updateUser } = useUserContext();

  const [rememberMe, setRememberMe] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleSignIn = async (event) => {
    event.preventDefault();

    if (!username.trim() || !password.trim()) {
      toast.error("Username and password are required", {
        duration: 2500,
        style: { background: "#ff4d4f", color: "#fff", fontSize: "12px" },
      });
      return;
    }

    setLoading(true);

    const response = await loginApi({ username, password });

    console.log("LOGIN API RESPONSE:", response);

    setLoading(false);

    if (response.status === "success") {
      // Store tokens in localStorage
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem("refreshToken", response.data.refresh_token);

      // Store full user data in localStorage and update context
      updateUser(response.data);

      toast.success("Login successful", { duration: 2000 });

      // Redirect based on role
      if (response.data.user_role === "admin") {
        navigate("/dashboard");
      } else {
        navigate("/clock-in-out");
      }

      return;
    }

    toast.error(response?.message || "Login Failed", {
      duration: 3000,
      style: { background: "#ff4d4f", color: "#fff", fontSize: "12px" },
    });
  };

  return (
    <BasicLayout image={bgImage}>
      <Toaster position="top-center" />

      <MDBox position="relative" zIndex={10} mb={4} textAlign="center">
        {/* Logo and title placeholders */}
      </MDBox>

      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Morgenroth Schulhaus
          </MDTypography>
          <MDTypography variant="h6" fontWeight="medium" color="white" mt={1}>
            Sign In
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSignIn}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Username"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </MDBox>

            <MDBox mb={2} sx={{ position: "relative" }}>
              <MDInput
                type={showPass ? "text" : "password"}
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <span
                onClick={() => setShowPass(!showPass)}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#888",
                }}
              >
                {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </span>
            </MDBox>

            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={loading || !username.trim() || !password.trim()}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "sign in"}
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account? Contact Admin
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
